import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

export interface BuildLogEntry {
  text: string;
  ts: number;
}

type Tone = 'ok' | 'fail' | 'start' | 'wait' | 'skip' | 'sub' | 'stage' | 'info';

export function logTone(text: string): Tone {
  if (text.trimStart().startsWith('↳')) return 'sub';
  if (text.startsWith('•')) return 'stage';
  if (text.startsWith('✓')) return 'ok';
  if (text.startsWith('✗')) return 'fail';
  if (text.startsWith('▶')) return 'start';
  if (text.startsWith('⏸')) return 'wait';
  if (text.startsWith('↷')) return 'skip';
  return 'info';
}

const toneClass: Record<Tone, string> = {
  ok: 'text-success-500',
  fail: 'text-red-400',
  start: 'text-primary-300',
  wait: 'text-amber-300',
  skip: 'text-gray-500',
  sub: 'text-gray-500 pl-4',
  stage: 'text-sky-300',
  info: 'text-gray-400',
};

// Latest Genesis step that has started ("▶ label") without a matching
// finish line (✓ / ✗ / ↷ with the same label).
function runningStep(logs: BuildLogEntry[]): { label: string; ts: number } | null {
  const open = new Map<string, number>();
  for (const l of logs) {
    const tone = logTone(l.text);
    const label = l.text.slice(1).trim().replace(/ \(\d+(?:\.\d+)?m?s\)$/, '').replace(/ (failed.*|skipped)$/, '');
    if (tone === 'start') open.set(label, l.ts);
    else if (tone === 'ok' || tone === 'fail' || tone === 'skip') open.delete(label);
  }
  let best: { label: string; ts: number } | null = null;
  for (const [label, ts] of open) if (!best || ts >= best.ts) best = { label, ts };
  return best;
}

const fmtTime = (ts: number) => new Date(ts).toLocaleTimeString([], { hour12: false });

function fmtElapsed(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

interface BuildConsoleProps {
  logs: BuildLogEntry[];
  progress: number;
  error: string | null;
  running: boolean;
}

/**
 * Build status for App Forge: a progress header (percent, current step,
 * elapsed time) above a terminal-style log with real per-line timestamps,
 * tone-colored lines and stick-to-bottom auto-scroll.
 */
export function BuildConsole({ logs, progress, error, running }: BuildConsoleProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [stick, setStick] = useState(true);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && stick) el.scrollTop = el.scrollHeight;
  }, [logs, stick]);

  const startedAt = logs[0]?.ts;
  const endedAt = running ? now : logs[logs.length - 1]?.ts;
  const running_ = runningStep(logs);
  const current = running_
    ? `${running_.label} · running ${fmtElapsed((running ? now : endedAt ?? now) - running_.ts)}`
    : [...logs].reverse().find((l) => !['ok', 'sub'].includes(logTone(l.text)))?.text ?? logs[logs.length - 1]?.text;
  const pct = Math.round(progress);
  const failed = !!error;

  return (
    <div className="w-full max-w-2xl flex flex-col gap-5 animate-fade-in">
      {/* Status header */}
      <div className={`rounded-2xl border p-5 bg-[#131722]/80 backdrop-blur-md ${failed ? 'border-red-500/40' : 'border-[#2a2e3d]'}`}>
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${failed ? 'bg-red-500/15 text-red-400' : 'bg-primary-500/15 text-primary-300'}`}>
            {failed ? <AlertTriangle className="w-6 h-6" /> : running ? <Loader2 className="w-6 h-6 animate-spin" /> : <CheckCircle2 className="w-6 h-6" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-white font-semibold text-lg">
                {failed ? 'Generation failed' : running ? 'Building your app' : 'Build finished'}
              </h3>
              <span className={`text-2xl font-bold tabular-nums ${failed ? 'text-red-300' : 'text-white'}`}>{pct}%</span>
            </div>
            <p className="text-sm text-gray-400 truncate" title={current}>
              {failed ? 'Files generated so far are kept under Deliverables.' : current ?? 'Initializing build pipeline…'}
            </p>
          </div>
        </div>
        <div className="mt-4 h-2 rounded-full bg-[#1f2330] overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ease-out ${failed ? 'bg-red-500/70' : 'bg-gradient-to-r from-[#6366f1] to-[#8b5cf6]'} ${running && !failed ? 'animate-pulse' : ''}`}
            style={{ width: `${Math.max(pct, 2)}%` }}
          />
        </div>
        {failed && (
          <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-200 font-mono break-words">
            {error}
          </div>
        )}
      </div>

      {/* Log console */}
      <div className="rounded-2xl border border-[#2a2e3d] overflow-hidden bg-[#0d0f16] shadow-2xl">
        <div className="px-4 py-2.5 border-b border-[#2a2e3d] flex items-center gap-2 bg-[#141722]">
          <span className="text-xs font-mono text-gray-400">Build log</span>
          <span className="text-[10px] font-mono text-gray-600">{logs.length} lines</span>
          {startedAt && (
            <span className="ml-auto text-[10px] font-mono text-gray-500 tabular-nums">elapsed {fmtElapsed((endedAt ?? startedAt) - startedAt)}</span>
          )}
        </div>
        <div
          ref={scrollRef}
          onScroll={(e) => {
            const el = e.currentTarget;
            setStick(el.scrollHeight - el.scrollTop - el.clientHeight < 24);
          }}
          className="h-[280px] overflow-y-auto px-4 py-3 font-mono text-xs leading-relaxed custom-scrollbar"
        >
          {logs.length === 0 && <div className="text-gray-600 animate-pulse">Initializing build pipeline…</div>}
          {logs.map((log, i) => {
            const tone = logTone(log.text);
            const last = i === logs.length - 1 && running;
            return (
              <div key={`${log.ts}-${i}`} className="flex gap-3 py-0.5">
                <span className="text-[#4a4f63] tabular-nums shrink-0">{fmtTime(log.ts)}</span>
                <span className={`break-words ${toneClass[tone]} ${last ? 'font-semibold' : ''}`}>{log.text}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
